import { useMutation, useQuery, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { ApiError } from '@/api/client';
import { fetchIdentity } from '@/api/trip';
import {
  authErrorMessage,
  createInvite,
  login,
  logout,
  register,
  type AuthSuccess,
  type InviteSuccess,
  type LoginInput,
  type RegisterInput,
} from '@/api/auth';
import { tripKeys } from '@/api/queryKeys';
import { LOGGED_IN_EVENT } from '@/lib/capacitorBridge';
import type { AuthIdentity } from '@/api/types';

/**
 * Wird nach erfolgreichem Login oder Register dispatcht. Capacitor-Bridge
 * lauscht und re-postet das FCM-Token, das beim Bridge-Init vor Login eine
 * 401-Antwort vom Backend bekam (User war zu dem Zeitpunkt noch nicht
 * authentifiziert).
 */
function notifyLoggedIn(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(LOGGED_IN_EVENT));
}

interface UseAuthResult {
  identity: AuthIdentity | undefined;
  isLoading: boolean;
  isAuthenticated: boolean;
  canInvite: boolean;
  loginMutation: UseMutationResult<AuthSuccess, ApiError, LoginInput>;
  registerMutation: UseMutationResult<AuthSuccess, ApiError, RegisterInput>;
  logoutMutation: UseMutationResult<{ ok: true }, ApiError, void>;
  inviteMutation: UseMutationResult<InviteSuccess, ApiError, void>;
}

/**
 * Zentrales Auth-Hook für den Trip-Kontext. Capslt die Identity-Query
 * (gecached via TanStack Query) plus Mutations für Login/Register/Logout/Invite.
 * Nach Login/Register/Logout wird der Identity-Cache invalidert; nach
 * Logout zusätzlich der gesamte Trip-Cache (alle nutzer-spezifischen Daten
 * sollen frisch geladen werden, sobald ein anderer User sich einloggt).
 */
export function useAuth(slug: string): UseAuthResult {
  const qc = useQueryClient();

  const identityQuery = useQuery({
    queryKey: tripKeys.identity(slug),
    queryFn: ({ signal }) => fetchIdentity(slug, signal),
  });

  const loginMutation = useMutation<AuthSuccess, ApiError, LoginInput>({
    mutationFn: (input) => login(slug, input),
    onSuccess: (data) => {
      qc.setQueryData<AuthIdentity>(tripKeys.identity(slug), {
        loggedIn: true,
        username: data.username,
        token: data.token,
        name: data.name,
      });
      notifyLoggedIn();
    },
  });

  const registerMutation = useMutation<AuthSuccess, ApiError, RegisterInput>({
    mutationFn: (input) => register(slug, input),
    onSuccess: (data) => {
      qc.setQueryData<AuthIdentity>(tripKeys.identity(slug), {
        loggedIn: true,
        username: data.username,
        token: data.token,
        name: data.name,
      });
      notifyLoggedIn();
    },
  });

  const logoutMutation = useMutation<{ ok: true }, ApiError, void>({
    mutationFn: () => logout(slug),
    onSuccess: () => {
      // Aller Trip-State (Posts, Votes, Chat, …) gehört dem User — bei Logout
      // wegwerfen, damit kein State zwischen zwei Logins leakt. Anschließend
      // Identity als „loggedIn: false" wieder seeden, damit RequireAuth direkt
      // korrekt redirected ohne nochmal /api/auth/me zu hitten.
      qc.removeQueries({ queryKey: tripKeys.all(slug), exact: false });
      qc.setQueryData<AuthIdentity>(tripKeys.identity(slug), { loggedIn: false });
    },
  });

  const inviteMutation = useMutation<InviteSuccess, ApiError, void>({
    mutationFn: () => createInvite(slug),
  });

  const identity = identityQuery.data;
  return {
    identity,
    isLoading: identityQuery.isLoading,
    isAuthenticated: identity?.loggedIn ?? false,
    canInvite: identity?.canInvite ?? false,
    loginMutation,
    registerMutation,
    logoutMutation,
    inviteMutation,
  };
}

/** Bequemer Helper: extrahiert den Backend-Error-Code aus einer ApiError-Instanz. */
export function authMutationError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string } | null;
    return authErrorMessage(body?.error);
  }
  if (err instanceof Error) return err.message;
  return 'Unbekannter Fehler.';
}
